import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { API, useAuth } from "../App";
import axios from "axios";
import { 
  Star, 
  Phone, 
  Mail,
  Award,
  Home,
  MapPin,
  Upload,
  X,
  Loader2,
  User,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Trophy,
  TrendingUp,
  Crown,
  Pencil,
  Trash2
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import Navbar from "../components/Navbar";

const AgentsPage = () => {
  const { user, login, loading: authLoading } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedPhoto, setUploadedPhoto] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    bio: "",
    license_number: "",
    specializations: "",
    experience_years: ""
  });
  
  // Review state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "", name: "", email: "" });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [agentReviews, setAgentReviews] = useState({});
  const [expandedReviews, setExpandedReviews] = useState({});
  const [editingReview, setEditingReview] = useState(null);
  const [editForm, setEditForm] = useState({ rating: 5, comment: "" });

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || "",
        email: user.email || ""
      }));
    }
  }, [user]);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents`);
      setAgents(response.data);
      // Fetch reviews for each agent
      response.data.forEach(agent => {
        fetchAgentReviews(agent.agent_id);
      });
    } catch (error) {
      console.error("Error fetching agents:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAgentReviews = async (agentId) => {
    try {
      const response = await axios.get(`${API}/reviews/${agentId}`);
      setAgentReviews(prev => ({
        ...prev,
        [agentId]: response.data
      }));
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  };
  
  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedAgent) return;
    
    // For guest reviews, require name and email
    const reviewerName = user?.name || reviewForm.name;
    const reviewerEmail = user?.email || reviewForm.email;
    
    if (!reviewerName || !reviewerEmail) {
      toast.error("Please enter your name and email");
      return;
    }
    
    setSubmittingReview(true);
    try {
      // Use guest endpoint if not logged in
      const endpoint = user ? `${API}/reviews` : `${API}/reviews/guest`;
      const config = user ? { withCredentials: true } : {};
      
      await axios.post(endpoint, {
        agent_id: selectedAgent.agent_id,
        reviewer_name: reviewerName,
        reviewer_email: reviewerEmail,
        rating: reviewForm.rating,
        comment: reviewForm.comment
      }, config);
      
      toast.success("Review submitted successfully! Thank you for your feedback.");
      setReviewDialogOpen(false);
      setReviewForm({ rating: 5, comment: "", name: "", email: "" });
      setSelectedAgent(null);
      // Refresh reviews
      fetchAgentReviews(selectedAgent.agent_id);
      fetchAgents();
    } catch (error) {
      if (error.response?.data?.detail?.includes("already reviewed")) {
        toast.error("You have already reviewed this agent");
      } else {
        toast.error(error.response?.data?.detail || "Failed to submit review");
      }
    } finally {
      setSubmittingReview(false);
    }
  };
  
  const toggleReviews = (agentId) => {
    setExpandedReviews(prev => ({
      ...prev,
      [agentId]: !prev[agentId]
    }));
  };

  const handleEditReview = async (reviewId, agentId) => {
    try {
      await axios.put(`${API}/reviews/${reviewId}`, editForm, { withCredentials: true });
      toast.success("Review updated");
      setEditingReview(null);
      fetchAgentReviews(agentId);
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update review");
    }
  };

  const handleDeleteReview = async (reviewId, agentId) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await axios.delete(`${API}/reviews/${reviewId}`, { withCredentials: true });
      toast.success("Review deleted");
      fetchAgentReviews(agentId);
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete review");
    }
  };
  
  const renderStars = (rating, interactive = false, onSelect = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type={interactive ? "button" : undefined}
            onClick={interactive && onSelect ? () => onSelect(star) : undefined}
            className={interactive ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"}
            disabled={!interactive}
          >
            <Star
              className={`w-5 h-5 ${
                star <= rating
                  ? "fill-gold text-gold"
                  : "fill-transparent text-muted-foreground"
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadedPhoto(response.data);
      toast.success("Photo uploaded!");
    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removePhoto = () => {
    setUploadedPhoto(null);
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    
    setSubmitting(true);
    try {
      // Use public endpoint if not logged in, authenticated endpoint if logged in
      const endpoint = user ? `${API}/agents` : `${API}/agents/public-apply`;
      
      await axios.post(endpoint, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        bio: formData.bio || null,
        photo: uploadedPhoto?.url || null,
        license_number: formData.license_number || null,
        specializations: formData.specializations || "",
        experience_years: parseInt(formData.experience_years) || 0
      }, { withCredentials: true });

      toast.success("Application submitted! You will be contacted once reviewed.");
      setApplyDialogOpen(false);
      localStorage.removeItem('pendingAgentApplication');
      setFormData({
        name: "",
        email: "",
        phone: "",
        bio: "",
        license_number: "",
        specializations: "",
        experience_years: ""
      });
      setUploadedPhoto(null);
    } catch (error) {
      if (error.response?.data?.detail === "You are already registered as an agent" || 
          error.response?.data?.detail === "This email is already registered as an agent") {
        toast.error("This email is already registered as an agent!");
      } else if (error.response?.data?.detail === "You already have a pending application" ||
                 error.response?.data?.detail === "An application with this email is already pending review") {
        toast.error("An application with this email is already pending. Please wait for approval.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to submit application");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const location = useLocation();
  const hasSubmittedRef = useRef(false);

  // Check for pending application after login and auto-submit
  useEffect(() => {
    if (user && !hasSubmittedRef.current) {
      const pendingApp = localStorage.getItem('pendingAgentApplication');
      if (pendingApp) {
        hasSubmittedRef.current = true;
        try {
          const savedData = JSON.parse(pendingApp);
          
          // Auto-submit the application
          const submitApplication = async () => {
            setSubmitting(true);
            try {
              await axios.post(`${API}/agents`, {
                name: savedData.name || user.name,
                email: savedData.email || user.email,
                phone: savedData.phone || null,
                bio: savedData.bio || null,
                photo: savedData.photo || null,
                license_number: savedData.license_number || null,
                specializations: savedData.specializations || "",
                experience_years: parseInt(savedData.experience_years) || 0
              }, { withCredentials: true });

              localStorage.removeItem('pendingAgentApplication');
              toast.success("Application submitted successfully! Please wait for administrator approval.");
            } catch (error) {
              localStorage.removeItem('pendingAgentApplication');
              if (error.response?.data?.detail === "You are already registered as an agent") {
                toast.error("You are already registered as an agent!");
              } else if (error.response?.data?.detail === "You already have a pending application") {
                toast.error("You already have a pending application. Please wait for approval.");
              } else {
                toast.error(error.response?.data?.detail || "Failed to submit application. Please try again.");
                // Open the dialog so they can try manually
                setFormData({
                  name: savedData.name || user.name || "",
                  email: savedData.email || user.email || "",
                  phone: savedData.phone || "",
                  bio: savedData.bio || "",
                  license_number: savedData.license_number || "",
                  specializations: savedData.specializations || "",
                  experience_years: savedData.experience_years || ""
                });
                if (savedData.photo) {
                  setUploadedPhoto({ url: savedData.photo });
                }
                setApplyDialogOpen(true);
              }
            } finally {
              setSubmitting(false);
            }
          };
          
          submitApplication();
        } catch (e) {
          localStorage.removeItem('pendingAgentApplication');
        }
      }
    }
  }, [user]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-gold font-medium tracking-widest uppercase text-sm mb-2">
              Our Team
            </p>
            <h1 className="text-3xl md:text-5xl font-serif mb-4">Meet Our Expert Agents</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our dedicated team of real estate professionals is here to help you find the perfect property 
              or sell your home for the best value.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Top Performers Leaderboard */}
      {!loading && agents.length >= 2 && (
        <section className="py-12 bg-gradient-to-b from-muted to-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="flex items-center justify-center gap-3 mb-8">
                <Trophy className="w-6 h-6 text-gold" />
                <h2 className="text-2xl font-serif">Top Performers</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Sort agents by total_sales and rating */}
                {[...agents]
                  .sort((a, b) => {
                    // Primary sort by total_sales, secondary by rating
                    if ((b.total_sales || 0) !== (a.total_sales || 0)) {
                      return (b.total_sales || 0) - (a.total_sales || 0);
                    }
                    return (b.rating || 0) - (a.rating || 0);
                  })
                  .slice(0, 3)
                  .map((agent, index) => (
                    <motion.div
                      key={agent.agent_id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className={`relative bg-card border rounded-sm p-6 text-center ${
                        index === 0 
                          ? "border-gold shadow-lg shadow-gold/10 md:order-2 md:-mt-4" 
                          : index === 1 
                            ? "border-border/60 md:order-1" 
                            : "border-border/60 md:order-3"
                      }`}
                      data-testid={`top-performer-${index + 1}`}
                    >
                      {/* Rank Badge */}
                      <div className={`absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 
                          ? "bg-gold text-black" 
                          : index === 1 
                            ? "bg-gray-300 text-gray-700" 
                            : "bg-amber-700 text-white"
                      }`}>
                        {index === 0 ? <Crown className="w-4 h-4" /> : index + 1}
                      </div>
                      
                      <img
                        src={agent.photo || "https://images.unsplash.com/photo-1633353237701-1bf9b7e866ff"}
                        alt={agent.name}
                        className={`w-20 h-20 mx-auto rounded-full object-cover mb-4 border-2 ${
                          index === 0 ? "border-gold" : "border-border"
                        }`}
                      />
                      
                      <h3 className="font-serif text-lg mb-1">{agent.name}</h3>
                      
                      <div className="flex items-center justify-center gap-1 text-gold mb-2">
                        <Star className="w-4 h-4 fill-current" />
                        <span className="font-medium">{agent.rating || 0}</span>
                      </div>
                      
                      <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <TrendingUp className="w-4 h-4 text-green-500" />
                          <span className="font-semibold text-foreground">{agent.total_sales || 0}</span>
                          <span>sales</span>
                        </div>
                      </div>
                      
                      {agent.specializations?.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-2">
                          {agent.specializations.slice(0, 2).join(" • ")}
                        </p>
                      )}
                    </motion.div>
                  ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Agents List */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[1, 2].map((i) => (
                <div key={i} className="bg-card rounded-sm p-6 animate-pulse">
                  <div className="flex gap-6">
                    <div className="w-40 h-40 bg-muted rounded-sm"></div>
                    <div className="flex-1">
                      <div className="h-6 bg-muted rounded w-1/2 mb-2"></div>
                      <div className="h-4 bg-muted rounded w-1/4 mb-4"></div>
                      <div className="h-20 bg-muted rounded"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : agents.length === 0 ? (
            <div className="text-center py-16">
              <Award className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-serif text-xl mb-2">No Agents Found</h3>
              <p className="text-muted-foreground">Our team is being updated. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {agents.map((agent, index) => (
                <motion.div
                  key={agent.agent_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-card border border-border/40 rounded-sm overflow-hidden hover:border-primary/30 transition-colors"
                  data-testid={`agent-detail-${agent.agent_id}`}
                >
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-48 h-48 md:h-auto flex-shrink-0">
                      <img
                        src={agent.photo || "https://images.unsplash.com/photo-1633353237701-1bf9b7e866ff"}
                        alt={agent.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-serif text-2xl mb-1">{agent.name}</h3>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1 text-gold">
                              <Star className="w-4 h-4 fill-current" />
                              <span className="font-medium">{agent.rating || 0}</span>
                              {agentReviews[agent.agent_id]?.review_count > 0 && (
                                <span className="text-muted-foreground">
                                  ({agentReviews[agent.agent_id]?.review_count} reviews)
                                </span>
                              )}
                            </div>
                            <span>•</span>
                            <span>{agent.total_sales || 0} sales</span>
                            <span>•</span>
                            <span>{agent.experience_years || 0}+ years</span>
                          </div>
                        </div>
                      </div>

                      <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                        {agent.bio}
                      </p>

                      {agent.specializations?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {agent.specializations.map((spec, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {spec}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {agent.license_number && (
                        <p className="text-xs text-muted-foreground mb-4">
                          License: {agent.license_number}
                        </p>
                      )}

                      <div className="flex gap-3 pt-4 border-t border-border">
                        <a 
                          href={`mailto:${agent.email}`}
                          className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                          Email
                        </a>
                        {agent.phone && (
                          <a 
                            href={`tel:${agent.phone}`}
                            className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                          >
                            <Phone className="w-4 h-4" />
                            Call
                          </a>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="ml-auto text-sm"
                          onClick={() => {
                            if (!user) {
                              toast.info("Please sign in to leave a review");
                              login();
                              return;
                            }
                            setSelectedAgent(agent);
                            setReviewDialogOpen(true);
                          }}
                          data-testid={`review-agent-btn-${agent.agent_id}`}
                        >
                          <MessageSquare className="w-4 h-4 mr-1" />
                          Review
                        </Button>
                      </div>
                      
                      {/* Reviews Section */}
                      {agentReviews[agent.agent_id]?.reviews?.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-border">
                          <button
                            onClick={() => toggleReviews(agent.agent_id)}
                            className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full"
                          >
                            {expandedReviews[agent.agent_id] ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                            {expandedReviews[agent.agent_id] ? "Hide" : "Show"} Reviews ({agentReviews[agent.agent_id]?.review_count})
                          </button>
                          
                          {expandedReviews[agent.agent_id] && (
                            <div className="mt-3 space-y-3 max-h-64 overflow-y-auto">
                              {agentReviews[agent.agent_id]?.reviews.map((review) => (
                                <div key={review.review_id} className="bg-muted/50 p-3 rounded-sm">
                                  {editingReview === review.review_id ? (
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium">Rating:</span>
                                        {renderStars(editForm.rating, true, (star) => setEditForm(prev => ({ ...prev, rating: star })))}
                                      </div>
                                      <Textarea
                                        value={editForm.comment}
                                        onChange={(e) => setEditForm(prev => ({ ...prev, comment: e.target.value }))}
                                        rows={2}
                                        className="text-sm"
                                        data-testid="edit-review-comment"
                                      />
                                      <div className="flex gap-2">
                                        <Button size="sm" onClick={() => handleEditReview(review.review_id, agent.agent_id)} data-testid="save-review-btn">Save</Button>
                                        <Button size="sm" variant="outline" onClick={() => setEditingReview(null)}>Cancel</Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium text-sm">{review.reviewer_name}</span>
                                        <div className="flex items-center gap-2">
                                          <div className="flex items-center gap-1">
                                            {[...Array(5)].map((_, i) => (
                                              <Star
                                                key={i}
                                                className={`w-3 h-3 ${
                                                  i < review.rating
                                                    ? "fill-gold text-gold"
                                                    : "fill-transparent text-muted-foreground"
                                                }`}
                                              />
                                            ))}
                                          </div>
                                          {user?.role === "admin" && (
                                            <div className="flex gap-1">
                                              <button
                                                onClick={() => { setEditingReview(review.review_id); setEditForm({ rating: review.rating, comment: review.comment }); }}
                                                className="p-1 text-muted-foreground hover:text-primary transition-colors rounded"
                                                data-testid={`edit-review-${review.review_id}`}
                                                title="Edit review"
                                              >
                                                <Pencil className="w-3.5 h-3.5" />
                                              </button>
                                              <button
                                                onClick={() => handleDeleteReview(review.review_id, agent.agent_id)}
                                                className="p-1 text-muted-foreground hover:text-destructive transition-colors rounded"
                                                data-testid={`delete-review-${review.review_id}`}
                                                title="Delete review"
                                              >
                                                <Trash2 className="w-3.5 h-3.5" />
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <p className="text-sm text-muted-foreground">{review.comment}</p>
                                      <p className="text-xs text-muted-foreground/60 mt-2">
                                        {new Date(review.created_at).toLocaleDateString()}
                                      </p>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-serif mb-4">Join Our Team</h2>
            <p className="text-white/80 max-w-2xl mx-auto mb-8">
              Are you a licensed real estate agent looking to join a prestigious brokerage? 
              We're always looking for talented professionals. You can also apply to become a
              <strong> Broker</strong> &mdash; our trusted escrow partners.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                variant="secondary" 
                className="bg-white text-primary hover:bg-white/90 active:scale-95 h-12 px-8 transition-all"
                data-testid="join-team-btn"
                onClick={() => {
                  setApplyDialogOpen(true);
                }}
              >
                <Award className="w-4 h-4 mr-2" />
                Apply as Agent
              </Button>
              <Link to="/apply/broker">
                <Button 
                  variant="outline" 
                  className="border-white text-white hover:bg-white hover:text-primary active:scale-95 h-12 px-8 transition-all w-full sm:w-auto"
                  data-testid="apply-as-broker-btn"
                >
                  Apply as Broker
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Review {selectedAgent?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReviewSubmit} className="space-y-4 mt-4">
            {/* Show name/email fields only for guests (not logged in) */}
            {!user && (
              <div className="space-y-3 pb-3 border-b">
                <div>
                  <label className="block text-sm font-medium mb-1">Your Name *</label>
                  <Input
                    required
                    value={reviewForm.name}
                    onChange={(e) => setReviewForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Your Email *</label>
                  <Input
                    required
                    type="email"
                    value={reviewForm.email}
                    onChange={(e) => setReviewForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Enter your email"
                  />
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-2">Your Rating *</label>
              <div className="flex gap-2">
                {renderStars(reviewForm.rating, true, (star) => 
                  setReviewForm(prev => ({ ...prev, rating: star }))
                )}
                <span className="ml-2 text-sm text-muted-foreground">
                  {reviewForm.rating} / 5
                </span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Your Review *</label>
              <Textarea
                required
                value={reviewForm.comment}
                onChange={(e) => setReviewForm(prev => ({ ...prev, comment: e.target.value }))}
                placeholder="Share your experience working with this agent..."
                rows={4}
                data-testid="review-comment-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button 
                type="submit" 
                className="flex-1" 
                disabled={submittingReview}
                data-testid="submit-review-btn"
              >
                {submittingReview ? "Submitting..." : "Submit Review"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setReviewDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Apply Dialog */}
      <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Apply to Join Our Team</DialogTitle>
          </DialogHeader>
          
          {/* Sign-in notice for unauthenticated users */}
          {!user && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> You'll need to sign in with Google to submit your application. 
                Fill out the form below and click Submit - you'll be prompted to sign in.
              </p>
            </div>
          )}
          
          <form onSubmit={handleApplySubmit} className="space-y-4 mt-4">
            {/* Photo Upload */}
            <div>
              <label className="block text-sm font-medium mb-2">Profile Photo</label>
              <div className="flex items-center gap-4">
                {uploadedPhoto ? (
                  <div className="relative">
                    <img 
                      src={uploadedPhoto.url} 
                      alt="Profile" 
                      className="w-24 h-24 rounded-full object-cover border-2 border-primary"
                    />
                    <button
                      type="button"
                      onClick={removePhoto}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center border-2 border-dashed border-border">
                    <User className="w-10 h-10 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    id="agent-photo-upload"
                  />
                  <label 
                    htmlFor="agent-photo-upload" 
                    className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 rounded-sm text-sm transition-colors"
                  >
                    {uploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    {uploading ? "Uploading..." : "Upload Photo"}
                  </label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Full Name *</label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <Input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+260 XXX XXX XXX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">License Number</label>
                <Input
                  value={formData.license_number}
                  onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                  placeholder="ZM-XXXXX"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Years of Experience</label>
                <Input
                  type="number"
                  value={formData.experience_years}
                  onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })}
                  placeholder="5"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Specializations</label>
                <Input
                  value={formData.specializations}
                  onChange={(e) => setFormData({ ...formData, specializations: e.target.value })}
                  placeholder="Residential, Land"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Bio / About You</label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Tell us about your experience and expertise..."
                rows={3}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1" disabled={submitting}>
                {submitting ? "Submitting..." : user ? "Submit Application" : "Sign In & Submit"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setApplyDialogOpen(false)}>
                Cancel
              </Button>
            </div>
            
            <p className="text-xs text-muted-foreground text-center">
              Your application will be reviewed by an administrator. You'll be notified once approved.
            </p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="py-12 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 bg-gold rounded-sm flex items-center justify-center">
              <Home className="w-5 h-5 text-black" />
            </div>
            <span className="font-serif text-xl font-semibold">Buyorent</span>
          </div>
          <p className="text-muted-foreground text-sm mb-4">
            Your trusted partner in Buyorent Real Estate
          </p>
          <p className="text-muted-foreground/60 text-xs">
            © 2025 Buyorent. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AgentsPage;
